import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { COOKIE_NONCE } from "@/constants/common";
import { setCookie } from "@/utils/cookies";
import type { ApiError } from "@/types/error";

let isRefreshing = false;
let requestQueue: ((newNonce: string | null, error?: any) => void)[] = [];

const createAxiosInstance = (
  baseURL: string,
  handleRequestConfig: (config: InternalAxiosRequestConfig) => Promise<InternalAxiosRequestConfig>,
  handleCustomResponseError?: (error: AxiosError) => ApiError | null,
) => {
  const axiosInstance = axios.create({
    baseURL,
    timeout: 10000,
  });

  const fetchNonceFromApi = async () => {
    try {
      const response = await axios.get(`${baseURL}/v1/cart/items`); // best to use a custom get nonce api
      const nonce = response.headers["nonce"];
      if (nonce) {
        const expiresDate = new Date(Date.now() + 12 * 60 * 60 * 1000); // 12H
        setCookie(COOKIE_NONCE, nonce, expiresDate);
      }
      return nonce;
    } catch (error) {
      // 錯誤會向上傳播，由呼叫方處理
      throw error;
    }
  };

  const retryRequests = (newNonce: string | null, error: any | null = null) => {
    requestQueue.forEach((retryReq) => {
      if (newNonce) {
        retryReq(newNonce);
      } else {
        retryReq(error);
      }
    });
    requestQueue = [];
  };

  const isNonceError = (error: AxiosError): boolean => {
    return (
      error.response?.data?.code === "woocommerce_rest_invalid_nonce" ||
      error.response?.status === 403
    );
  };

  const handleNonceRefresh = async (
    originalRequest: InternalAxiosRequestConfig,
    axiosInstance: any,
    fetchNonceFromApi: () => Promise<string>,
  ): Promise<any> => {
    if (isRefreshing) {
      // 同時 nonce 無效的其他請求先佇列起來
      return new Promise((resolve, reject) => {
        requestQueue.push((newNonce, queueError) => {
          if (newNonce) {
            originalRequest.headers["nonce"] = newNonce;
            resolve(axiosInstance(originalRequest));
          } else {
            reject(queueError);
          }
        });
      });
    }

    isRefreshing = true;

    try {
      const newNonce = await fetchNonceFromApi();
      originalRequest.headers["nonce"] = newNonce;

      retryRequests(newNonce); // 等第一次取得 newNonce，消化先前同時 nonce 無效的其他請求
      return axiosInstance(originalRequest);
    } catch (fetchError) {
      retryRequests(null, fetchError); // 無法取得新 nonce 時，中止佇列的請求
      const axiosError = fetchError as AxiosError;
      const formattedError: ApiError = {
        status: axiosError?.response?.status ?? 500,
        data: axiosError?.response?.data,
        message: axiosError?.message || "無法更新 Nonce，請重新整理頁面",
      };
      throw formattedError;
    } finally {
      isRefreshing = false;
    }
  };

  const handleResponseError = async (
    error: AxiosError,
    handleCustomResponseError?: (error: AxiosError) => ApiError | null,
  ): Promise<ApiError> => {
    // 已取消的請求
    if (axios.isCancel(error) || error.name === "CanceledError") {
      return Promise.reject({ isAborted: true } as ApiError);
    }

    // nonce 無效重取（優先處理）
    const originalRequest = error.config;
    if (isNonceError(error) && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true; // 標記避免 refresh 失敗無限重試 (自訂 config 屬性)
      return handleNonceRefresh(originalRequest, axiosInstance, fetchNonceFromApi);
    }

    // 自訂錯誤處理
    if (handleCustomResponseError) {
      const customError = handleCustomResponseError(error);
      if (customError) {
        return Promise.reject(customError);
      }
    }

    // 請求已成功發出，且伺服器也響應了狀態碼，但狀態碼超出 2xx 範圍
    if (error.response) {
      const formattedError: ApiError = {
        status: error.response.status,
        data: error.response.data,
        message: error.message || "伺服器回應錯誤",
      };
      return Promise.reject(formattedError);
    }

    // 請求已成功發出，但没有收到回應（網路錯誤或超時）
    const formattedError: ApiError = {
      status: error.request?.status ?? 0,
      message: error.message || "無法連線到伺服器，請檢查網路連線",
    };

    // 如果是超時錯誤
    if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
      formattedError.message = "請求逾時，請稍候再試";
    }

    return Promise.reject(formattedError);
  };

  axiosInstance.interceptors.request.use(async (config) => await handleRequestConfig(config));

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => handleResponseError(error, handleCustomResponseError),
  );

  return axiosInstance;
};

export default createAxiosInstance;
